import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useToast } from '@/hooks/use-toast'
import { useAuthStore } from '@/app/store/auth'
import api from '@/lib/api/axios'

interface ChangePasswordRequest {
    currentPassword: string
    newPassword: string
}

interface ChangePasswordResponse {
    success: boolean
    message: string
}


export function useChangePassword() {
    const queryClient = useQueryClient()
    const { toast } = useToast()
    const { user } = useAuthStore()

    return useMutation({
        mutationFn: async (data: ChangePasswordRequest): Promise<ChangePasswordResponse> => {
            // Get fresh user data from auth store at the time of the API call
            const authState = useAuthStore.getState()
            const currentUser = authState.user

            // No debug dump of the auth state here: it carries mfaSetupToken, the short-lived
            // token that proves the password step, and this ran on every password change.

            if (!currentUser?.id) {
                throw new Error('User not authenticated')
            }

            try {
                console.log('🔑 PASSWORD CHANGE - Making API call to:', `/api/users/${currentUser.id}/password`)
                const response = await api.put(`/api/users/${currentUser.id}/password`, {
                    currentPassword: data.currentPassword,
                    newPassword: data.newPassword
                })

                return {
                    success: true,
                    message: response.data.message || 'Password changed successfully'
                }
            } catch (error: any) {
                // Handle axios error response
                if (error.response?.data?.error) {
                    throw new Error(error.response.data.error)
                } else if (error.response?.data?.message) {
                    throw new Error(error.response.data.message)
                } else {
                    throw new Error(error.message || 'Failed to change password')
                }
            }
        },
        onSuccess: () => {
            // Invalidate user queries to refresh user data
            queryClient.invalidateQueries({ queryKey: ['users'] })
            queryClient.invalidateQueries({ queryKey: ['profile'] })

            toast({
                title: 'Password Changed',
                description: 'Your password has been changed successfully.',
            })
        },
        onError: (error: Error) => {
            toast({
                title: 'Error',
                description: error.message || 'Failed to change password. Please try again.',
                variant: 'destructive',
            })
        },
    })
}
